import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Trash2, Edit2, Crown, Edit3, Eye, X, Check, Wifi, Lock, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function getHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
}

const ROLE_CONFIG = {
  super_admin: { label: 'Super Admin', icon: Crown, badgeClass: 'bg-red-100 text-red-700' },
  editor: { label: 'Editor', icon: Edit3, badgeClass: 'bg-amber-100 text-amber-700' },
  viewer: { label: 'Viewer', icon: Eye, badgeClass: 'bg-slate-100 text-slate-600' },
};

function ChangePasswordPanel() {
  const [current, setCurrent] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = async (e) => {
    e.preventDefault();
    if (newPw !== confirm) { toast.error('New passwords do not match'); return; }
    if (newPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify({ current_password: current, new_password: newPw }) });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); setLoading(false); return; }
      toast.success('Password changed successfully');
      setCurrent(''); setNewPw(''); setConfirm('');
    } catch { toast.error('Connection error'); }
    setLoading(false);
  };

  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm" data-testid="change-password-panel">
      <div className="flex items-center gap-2 mb-4">
        <Lock className="w-5 h-5 text-blue-600" />
        <h3 className="text-sm font-bold text-slate-800">Change My Password</h3>
      </div>
      <form onSubmit={handleChange} className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500">Current Password</label>
          <input type="password" value={current} onChange={e => setCurrent(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" required />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">New Password</label>
          <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" required minLength={6} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500">Confirm New Password</label>
          <div className="flex gap-2 mt-1">
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" required />
            <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 whitespace-nowrap disabled:opacity-50">
              {loading ? '...' : 'Update'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function ResetRequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [newPw, setNewPw] = useState('');
  const [approvingId, setApprovingId] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.role === 'super_admin';

  const fetchRequests = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await fetch(`${API}/api/auth/password-reset-requests`, { headers: getHeaders() });
      if (res.ok) setRequests(await res.json());
    } catch { /* ignore */ }
  }, [isSuperAdmin]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleApprove = async (reqId) => {
    if (!newPw || newPw.length < 6) { toast.error('Set a password (min 6 chars)'); return; }
    try {
      const res = await fetch(`${API}/api/auth/password-reset-requests/${reqId}/approve`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ new_password: newPw }) });
      if (!res.ok) { toast.error('Failed'); return; }
      toast.success('Password reset approved');
      setApprovingId(null); setNewPw('');
      fetchRequests();
    } catch { toast.error('Connection error'); }
  };

  const handleReject = async (reqId) => {
    try {
      const res = await fetch(`${API}/api/auth/password-reset-requests/${reqId}/reject`, { method: 'POST', headers: getHeaders() });
      if (!res.ok) { toast.error('Failed'); return; }
      toast.success('Request rejected');
      fetchRequests();
    } catch { toast.error('Connection error'); }
  };

  if (!isSuperAdmin) return null;
  if (requests.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 shadow-sm" data-testid="reset-requests-panel">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-5 h-5 text-amber-600" />
        <h3 className="text-sm font-bold text-amber-800">Password Reset Requests ({requests.length})</h3>
      </div>
      <div className="space-y-2">
        {requests.map(r => (
          <div key={r.id} className="flex items-center justify-between bg-white rounded-lg border border-amber-200 px-4 py-3">
            <div>
              <span className="text-sm font-medium text-slate-800">{r.user_name}</span>
              <span className="text-xs text-slate-500 ml-2">{r.email}</span>
              <span className={`ml-2 px-1.5 py-0.5 text-[10px] rounded-full ${ROLE_CONFIG[r.user_role]?.badgeClass || 'bg-slate-100'}`}>{r.user_role}</span>
              <span className="text-[10px] text-slate-400 ml-2">{new Date(r.created_at).toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-2">
              {approvingId === r.id ? (
                <>
                  <input type="text" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="New password" className="border rounded px-2 py-1 text-xs w-32" autoFocus />
                  <button onClick={() => handleApprove(r.id)} className="p-1.5 bg-emerald-100 hover:bg-emerald-200 rounded text-emerald-700" title="Confirm"><Check className="w-4 h-4" /></button>
                  <button onClick={() => { setApprovingId(null); setNewPw(''); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-400"><X className="w-4 h-4" /></button>
                </>
              ) : (
                <>
                  <button onClick={() => { setApprovingId(r.id); setNewPw(''); }} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700">
                    <CheckCircle className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button onClick={() => handleReject(r.id)} className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 text-xs font-medium rounded-lg hover:bg-red-200">
                    <XCircle className="w-3.5 h-3.5" /> Reject
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'viewer' });

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/auth/users`, { headers: getHeaders() });
      if (res.ok) setUsers(await res.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error('All fields required'); return; }
    try {
      const res = await fetch(`${API}/api/auth/users`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success('User created');
      setShowAdd(false);
      setForm({ name: '', email: '', password: '', role: 'viewer' });
      fetchUsers();
    } catch { toast.error('Connection error'); }
  };

  const handleUpdate = async (userId) => {
    const update = {};
    if (form.name) update.name = form.name;
    if (form.email) update.email = form.email;
    if (form.password) update.password = form.password;
    if (form.role) update.role = form.role;
    try {
      const res = await fetch(`${API}/api/auth/users/${userId}`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify(update) });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success('User updated');
      setEditingId(null);
      setForm({ name: '', email: '', password: '', role: 'viewer' });
      fetchUsers();
    } catch { toast.error('Connection error'); }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"?`)) return;
    try {
      const res = await fetch(`${API}/api/auth/users/${userId}`, { method: 'DELETE', headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success('User deleted');
      fetchUsers();
    } catch { toast.error('Connection error'); }
  };

  const startEdit = (user) => {
    setEditingId(user.id);
    setForm({ name: user.name, email: user.email, password: '', role: user.role });
    setShowAdd(false);
  };

  return (
    <div data-testid="user-management-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">User Management</h1>
          <p className="text-sm text-slate-500">Manage accounts, passwords, and roles</p>
        </div>
        <button
          onClick={() => { setShowAdd(true); setEditingId(null); setForm({ name: '', email: '', password: '', role: 'viewer' }); }}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-900"
          data-testid="add-user-btn"
        >
          <UserPlus className="w-4 h-4" /> Add User
        </button>
      </div>

      {/* Password Reset Requests (super_admin only) */}
      <div className="mb-5"><ResetRequestsPanel /></div>

      {/* Change My Password */}
      <div className="mb-5"><ChangePasswordPanel /></div>

      {/* Add User Form */}
      {showAdd && (
        <div className="bg-white border rounded-xl p-5 mb-5 shadow-sm" data-testid="add-user-form">
          <h3 className="text-sm font-bold text-slate-700 mb-3">New User</h3>
          <form onSubmit={handleAdd} className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Name *</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" placeholder="Full name" data-testid="user-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Email *</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" placeholder="email@company.com" data-testid="user-email-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Password *</label>
              <input type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" placeholder="Password" data-testid="user-password-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Role *</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="user-role-select">
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
              </select>
            </div>
            <div className="col-span-2 flex justify-end gap-2 mt-1">
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="save-user-btn">Create User</button>
            </div>
          </form>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
        <table className="w-full" data-testid="users-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Name</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Email</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Role</th>
              <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map(u => {
              const rc = ROLE_CONFIG[u.role] || ROLE_CONFIG.viewer;
              const RIcon = rc.icon;
              const isSA = u.role === 'super_admin';
              return (
                <tr key={u.id} className="hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                  {editingId === u.id ? (
                    <>
                      <td className="px-5 py-3">
                        <input type="text" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                          className="border rounded px-2 py-1 text-sm w-full" />
                      </td>
                      <td className="px-5 py-3">
                        <input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
                          className="border rounded px-2 py-1 text-sm w-full" />
                      </td>
                      <td className="px-5 py-3">
                        <select value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))}
                          className="border rounded px-2 py-1 text-sm" disabled={isSA}>
                          <option value="viewer">Viewer</option>
                          <option value="editor">Editor</option>
                          {isSA && <option value="super_admin">Super Admin</option>}
                        </select>
                      </td>
                      <td className="px-5 py-3 text-center">
                        {u.is_online ? (
                          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-semibold"><Wifi className="w-3 h-3" /> Online</span>
                        ) : (
                          <span className="text-[10px] text-slate-400">Offline</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <input type="text" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))}
                            placeholder="New password (optional)" className="border rounded px-2 py-1 text-xs w-32" />
                          <button onClick={() => handleUpdate(u.id)} className="p-1.5 hover:bg-green-50 rounded text-green-600" title="Save">
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditingId(null)} className="p-1.5 hover:bg-slate-100 rounded text-slate-400" title="Cancel">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-5 py-3 text-sm font-medium text-slate-800">
                        <div className="flex items-center gap-2">
                          {isSA && <Crown className="w-3.5 h-3.5 text-red-500" />}
                          {u.name}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-sm text-slate-500">{u.email}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full ${rc.badgeClass}`}>
                          <RIcon className="w-3 h-3" />
                          {rc.label}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-center">
                        {u.is_online ? (
                          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-semibold" data-testid={`online-${u.id}`}>
                            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Online
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400">Offline</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        {!isSA && (
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => startEdit(u)} className="p-1.5 hover:bg-blue-50 rounded text-slate-400 hover:text-blue-600" data-testid={`edit-user-${u.id}`}>
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(u.id, u.name)} className="p-1.5 hover:bg-red-50 rounded text-slate-400 hover:text-red-600" data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
            {users.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-slate-400">No users found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
